import React, { useState } from "react";
import styles from "./styles.module.scss";
import ImageCropper from "../ImageCropper";
import ModalLayout from "../Layouts/ModalLayout/ModalLayout";
import closeIcon from "../../../public/images/close-icon.svg";
import { useTranslation } from "react-i18next";
import Button from "app/components/Button/ButtonVariant";
import { dataUrlToBlob } from "app/utils/file";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  imageFile: any;
  onChange: (image: any) => void;
}

const CoachCoverUpload = ({
  isOpen,
  onClose,
  imageSrc,
  imageFile,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const handleCrop = (data: string) => {
    if (data) {
      setCroppedImage(data);
    }
  };

  /**
   * The shape the banner is actually shown in, so the crop box is the frame.
   *
   * It used to crop 1500×500 — 3:1 — while every page that shows the cover draws
   * it as a full-width strip 200px tall (the facility landing page's header and
   * the booking pages' masthead; 1440×200 in the CN-2652 frames). `cover` then
   * scaled the 3:1 picture to the strip's width and threw away the rest of its
   * height: at 1440px wide, 480px of picture into a 200px strip — 58% of what the
   * coach had just chosen. That is the "we cut more than we showed" report.
   *
   * 7.2:1 is that strip, at twice its size so the banner stays sharp on a retina
   * screen. A full-bleed strip has no single aspect — a viewport wider than the
   * frame's 1440px still trims a little top and bottom, a narrower one a little
   * left and right — but at the width the page is designed for, what the coach
   * crops is exactly what the page shows.
   */
  const COVER_IMAGE_SIZE = {
    width: 2880,
    height: 400,
  };

  const onCancel = () => {
    setCroppedImage(null);
    onClose();
  };

  const onConfirmCrop = () => {
    const file = imageFile;

    if (croppedImage && onChange) {
      file.url = croppedImage;
      file.blob = dataUrlToBlob(croppedImage, file.type);
      onChange(file);
    }

    setCroppedImage(null);
    onClose();
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.title_container}>
          <h1>{t("Adjust Your Picture")}</h1>
          <img
            src={closeIcon}
            alt="close"
            className={styles.close_icon}
            onClick={onClose}
          />
        </div>
        <div className={styles.cropper_container}>
          <ImageCropper
            width={COVER_IMAGE_SIZE.width}
            height={COVER_IMAGE_SIZE.height}
            src={imageSrc}
            onCrop={(data: string) => handleCrop(data)}
          />
        </div>
        <div className={styles.footer}>
          <Button
            title={t("Cancel")}
            buttonType="secondary-outline"
            onClickFunc={onCancel}
          />
          <Button
            title={t("Set Cover")}
            buttonType="primary"
            onClickFunc={onConfirmCrop}
          />
        </div>
      </div>
    </ModalLayout>
  );
};

export default CoachCoverUpload;
